import { useState } from 'react';
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export function OutputViewer({ data, latency, error }) {
  const [view, setView] = useState('formatted');
  const [expanded, setExpanded] = useState(false);

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success('Copied to clipboard');
  };

  if (error) {
    // Parse structured error if it's JSON
    let parsed = null;
    try {
      if (typeof error === 'string' && error.startsWith('{')) parsed = JSON.parse(error);
      else if (typeof error === 'object') parsed = error;
    } catch {}

    // Check if error detail is a structured object (from our gateway)
    if (!parsed && typeof error === 'string') {
      try {
        // FastAPI sometimes wraps the dict as a string
        const match = error.match(/\{.*\}/s);
        if (match) parsed = JSON.parse(match[0].replace(/'/g, '"'));
      } catch {}
    }

    if (parsed && (parsed.reference_id || parsed.error)) {
      return (
        <div data-testid="playground-error" className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 space-y-3">
          <p className="text-sm font-medium text-destructive">{parsed.message || parsed.error || 'Your request could not be processed.'}</p>
          {parsed.reference_id && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] font-mono border-destructive/30 text-destructive">{parsed.reference_id}</Badge>
              <span className="text-[10px] text-muted-foreground">Save this ID if you need to contact support</span>
            </div>
          )}
          {parsed.retry_after && <p className="text-[10px] text-muted-foreground">Retry after {parsed.retry_after} seconds</p>}
          {parsed.suggestions && parsed.suggestions.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] text-muted-foreground font-medium">What you can try:</p>
              {parsed.suggestions.map((s, i) => (
                <p key={i} className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                  <span className="text-primary">&#8226;</span> {s}
                </p>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div data-testid="playground-error" className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 space-y-2">
        <p className="text-sm font-medium text-destructive">Something went wrong</p>
        <p className="text-xs text-destructive/80">{typeof error === 'string' ? error : JSON.stringify(error)}</p>
        <p className="text-[10px] text-muted-foreground">Check: Is the service in your subscription? Is your API key correct? Is the input valid?</p>
      </div>
    );
  }

  if (!data) return null;

  const hasMarkdown = typeof data.content === 'string' || typeof data.markdown === 'string';
  const hasElements = Array.isArray(data.elements) && data.elements.length > 0;
  const hasChunks = Array.isArray(data.chunks) && data.chunks.length > 0;
  const hasSegments = Array.isArray(data.segments) && data.segments.length > 0;
  const hasConfidence = data.confidence && typeof data.confidence === 'object';
  const hasBookmarks = Array.isArray(data.bookmarks) && data.bookmarks.length > 0;
  const hasLinks = Array.isArray(data.links) && data.links.length > 0;

  return (
    <div data-testid="playground-output" className="space-y-3">
      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Success</Badge>
          {latency && <span className="text-[10px] text-muted-foreground font-mono">{latency}ms</span>}
          {data.parser && <span className="text-[10px] text-muted-foreground">via {data.parser}</span>}
          {data.page_count !== undefined && <span className="text-[10px] text-muted-foreground">{data.page_count} pages</span>}
          {data.element_count !== undefined && <span className="text-[10px] text-muted-foreground">{data.element_count} elements</span>}
          {data.chunk_count !== undefined && <span className="text-[10px] text-muted-foreground">{data.chunk_count} chunks</span>}
          {data.num_speakers !== undefined && <span className="text-[10px] text-muted-foreground">{data.num_speakers} speakers</span>}
          {data.language && <span className="text-[10px] text-muted-foreground">lang: {data.language}</span>}
          {hasConfidence && data.confidence.mean_grade && (
            <Badge variant="outline" className={`text-[9px] ${data.confidence.mean_grade === 'excellent' ? 'border-emerald-500/40 text-emerald-400' : data.confidence.mean_grade === 'good' ? 'border-blue-500/40 text-blue-400' : data.confidence.mean_grade === 'fair' ? 'border-amber-500/40 text-amber-400' : 'border-red-500/40 text-red-400'}`}>
              {data.confidence.mean_grade}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {hasMarkdown && (
            <>
              <Button size="sm" variant={view === 'formatted' ? 'default' : 'ghost'} className="h-6 text-[10px]" onClick={() => setView('formatted')}>Preview</Button>
              <Button size="sm" variant={view === 'json' ? 'default' : 'ghost'} className="h-6 text-[10px]" onClick={() => setView('json')}>JSON</Button>
            </>
          )}
          <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={copyJson}><Copy className="w-3 h-3" /></Button>
        </div>
      </div>

      {/* Formatted view */}
      {view === 'formatted' && hasMarkdown && (
        <div className="p-4 rounded-xl bg-secondary/20 border border-border/20 text-sm leading-relaxed max-h-96 overflow-y-auto whitespace-pre-wrap font-mono text-xs">
          {typeof data.content === 'string' ? data.content : data.markdown}
        </div>
      )}

      {/* Elements view */}
      {view === 'formatted' && hasElements && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Detected Elements ({data.elements.length})</p>
          <div className="max-h-72 overflow-y-auto space-y-1">
            {data.elements.slice(0, expanded ? undefined : 10).map((el, i) => (
              <div key={i} className="p-2 rounded-lg bg-secondary/10 border border-border/10 text-xs space-y-1">
                <div className="flex items-start gap-2">
                  <Badge variant="outline" className="text-[9px] shrink-0 mt-0.5">{el.type}</Badge>
                  <span className="flex-1 truncate">{el.text || el.caption || el.description || '(no text)'}</span>
                  {el.confidence !== undefined && el.confidence !== null && (
                    <span className={`text-[9px] font-mono shrink-0 ${el.confidence > 0.8 ? 'text-emerald-400' : el.confidence > 0.6 ? 'text-amber-400' : 'text-destructive'}`}>
                      {(el.confidence * 100).toFixed(0)}%
                    </span>
                  )}
                  {el.page && <span className="text-[9px] text-muted-foreground shrink-0">p.{el.page}</span>}
                </div>
                {el.image_data && (
                  <img src={`data:image/png;base64,${el.image_data}`} alt={el.caption || 'Image'} className="max-w-xs max-h-40 rounded border border-border/20 mt-1" />
                )}
                {el.caption && el.type === 'picture' && <p className="text-[9px] text-muted-foreground italic">{el.caption}</p>}
                {el.hyperlink && <a href={el.hyperlink} target="_blank" rel="noreferrer" className="text-[9px] text-primary underline">{el.hyperlink}</a>}
                {el.data && el.data.headers && (
                  <div className="overflow-x-auto mt-1">
                    <table className="text-[9px] border-collapse">
                      <thead><tr>{el.data.headers.map((h, j) => <th key={j} className="border border-border/30 px-1.5 py-0.5 bg-secondary/30 text-left">{h}</th>)}</tr></thead>
                      <tbody>{(el.data.rows || []).slice(0, 5).map((row, j) => <tr key={j}>{row.map((cell, k) => <td key={k} className="border border-border/30 px-1.5 py-0.5">{String(cell)}</td>)}</tr>)}</tbody>
                    </table>
                    {el.data.num_rows > 5 && <p className="text-[9px] text-muted-foreground mt-0.5">...and {el.data.num_rows - 5} more rows</p>}
                  </div>
                )}
              </div>
            ))}
            {data.elements.length > 10 && (
              <Button size="sm" variant="ghost" className="w-full h-6 text-[10px]" onClick={() => setExpanded(!expanded)}>
                {expanded ? <><ChevronUp className="w-3 h-3 mr-1" />Show less</> : <><ChevronDown className="w-3 h-3 mr-1" />Show all {data.elements.length} elements</>}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Chunks view */}
      {view === 'formatted' && hasChunks && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">RAG Chunks ({data.chunks.length})</p>
          <div className="max-h-72 overflow-y-auto space-y-1.5">
            {data.chunks.slice(0, expanded ? undefined : 5).map((c, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-secondary/10 border border-border/10 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">Chunk {c.index}</Badge>
                  {c.page && <span className="text-[9px] text-muted-foreground">Page {c.page}</span>}
                  {c.headings && c.headings.length > 0 && (
                    <span className="text-[9px] text-primary truncate">{c.headings.join(' > ')}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground line-clamp-3">{c.text}</p>
              </div>
            ))}
            {data.chunks.length > 5 && (
              <Button size="sm" variant="ghost" className="w-full h-6 text-[10px]" onClick={() => setExpanded(!expanded)}>
                {expanded ? <><ChevronUp className="w-3 h-3 mr-1" />Show less</> : <><ChevronDown className="w-3 h-3 mr-1" />Show all {data.chunks.length} chunks</>}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Speaker segments (diarized transcription) */}
      {view === 'formatted' && hasSegments && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Speaker Segments ({data.segments.length}){data.num_speakers ? ` — ${data.num_speakers} speaker(s)` : ''}</p>
          <div className="max-h-72 overflow-y-auto space-y-1">
            {data.segments.slice(0, expanded ? undefined : 15).map((seg, i) => (
              <div key={i} className="p-2 rounded-lg bg-secondary/10 border border-border/10 text-xs flex items-start gap-2">
                {seg.speaker && <Badge className={`text-[9px] shrink-0 ${seg.speaker === 'SPEAKER_00' ? 'bg-blue-500/20 text-blue-400' : seg.speaker === 'SPEAKER_01' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'}`}>{seg.speaker}</Badge>}
                <span className="flex-1">{seg.text}</span>
                {seg.start !== undefined && <span className="text-[9px] text-muted-foreground shrink-0">{seg.start.toFixed(1)}s-{seg.end.toFixed(1)}s</span>}
              </div>
            ))}
            {data.segments.length > 15 && (
              <Button size="sm" variant="ghost" className="w-full h-6 text-[10px]" onClick={() => setExpanded(!expanded)}>
                {expanded ? <><ChevronUp className="w-3 h-3 mr-1" />Show less</> : <><ChevronDown className="w-3 h-3 mr-1" />Show all {data.segments.length} segments</>}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Confidence scores */}
      {view === 'formatted' && hasConfidence && data.confidence.layout_score != null && (
        <div className="p-3 rounded-lg bg-secondary/10 border border-border/10 space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Conversion Quality</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
            {data.confidence.layout_score != null && <div><span className="text-muted-foreground">Layout:</span> <span className="font-mono font-bold">{(data.confidence.layout_score * 100).toFixed(0)}%</span></div>}
            {data.confidence.ocr_score != null && <div><span className="text-muted-foreground">OCR:</span> <span className="font-mono font-bold">{(data.confidence.ocr_score * 100).toFixed(0)}%</span></div>}
            {data.confidence.parse_score != null && <div><span className="text-muted-foreground">Parse:</span> <span className="font-mono font-bold">{(data.confidence.parse_score * 100).toFixed(0)}%</span></div>}
            {data.confidence.mean_grade && <div><span className="text-muted-foreground">Grade:</span> <span className="font-bold">{data.confidence.mean_grade}</span></div>}
          </div>
        </div>
      )}

      {/* Metadata (bookmarks, links) */}
      {view === 'formatted' && (hasBookmarks || hasLinks) && (
        <div className="p-3 rounded-lg bg-secondary/10 border border-border/10 space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Extracted Metadata</p>
          {data.format && <p className="text-[10px] text-muted-foreground">Format: {data.format}</p>}
          {hasBookmarks && <p className="text-[10px]">{data.bookmarks.length} bookmarks</p>}
          {hasLinks && <p className="text-[10px]">{data.links.length} links</p>}
          {data.metadata && typeof data.metadata === 'object' && (
            <div className="grid grid-cols-2 gap-1 text-[10px] mt-1">
              {Object.entries(data.metadata).filter(([,v]) => v).slice(0, 6).map(([k, v]) => (
                <div key={k}><span className="text-muted-foreground">{k}:</span> <span>{String(v).slice(0, 60)}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Raw JSON view */}
      {(view === 'json' || (!hasMarkdown && !hasElements && !hasChunks && !hasSegments && !hasBookmarks && !hasLinks)) && (
        <pre data-testid="playground-json" className="bg-background border border-border/50 rounded-xl p-4 overflow-auto text-xs font-mono max-h-96">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
